import React, { useState, useEffect, useRef } from "react";
import apiClient from "../../AppClient";
import { Container, Form, Button, Table, Alert, Spinner, Modal, Pagination } from "react-bootstrap";
import { FaSearch } from "react-icons/fa";

const CustomerTypes = () => {
  const [customertypes, setCustomertypes] = useState([]);
  const [filteredCustomertypes, setFilteredCustomertypes] = useState([]);
  const [newExpenseName, setNewExpenseName] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState({ id: null, name: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 10;

  const nameRef = useRef(null);

  // Fetch all customertypes
  const fetchCustomertypes = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/customertypes/");
      setCustomertypes(response.data);
      setFilteredCustomertypes(response.data);
      setError(null);
    } catch (error) {
      setError(error.response?.data?.detail || "فشل في تحميل أنواع الزبائن.");
    } finally {
      setLoading(false);
    }
  };

  // Search functionality
  useEffect(() => {
    const filtered = customertypes.filter(expense =>
      expense.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCustomertypes(filtered);
    setCurrentPage(1);
  }, [searchTerm, customertypes]);

  // Add a new expense
  const addExpense = async () => {
    const name = nameRef.current.value;

    if (!name) {
      return setError("الرجاء إدخال نوع الزبون");
    }

    try {
      await apiClient.post("/customertypes/add/", {
        name: newExpenseName,
      });
      fetchCustomertypes();
      setNewExpenseName("");
      setError(null);
      setCurrentPage(1); // Reset to first page after adding new item
    } catch (error) {
      setError(error.response?.data?.detail || "فشل في إضافة نوع الزبون.");
    }
  };

  // Open edit modal
  const openEditModal = (expense) => {
    setSelectedExpense({ id: expense.id, name: expense.name });
    setShowEditModal(true);
  };

  // Save edited expense
  const saveEditedExpense = async () => {
    if (!selectedExpense.name) {
      return setError("الرجاء إدخال نوع الزبون");
    }

    try {
      await apiClient.put(`/customertypes/${selectedExpense.id}/update/`, {
        name: selectedExpense.name,
      });
      fetchCustomertypes();
      setShowEditModal(false);
      setError(null);
    } catch (error) {
      setError(error.response?.data?.detail || "فشل في تحديث نوع الزبون.");
    }
  };

  // Delete a expense
  const deleteExpense = async (id) => {
    const confirmDelete = window.confirm("هل أنت متأكد من حذف نوع الزبون؟");
    if (!confirmDelete) return;

    try {
      await apiClient.delete(`/customertypes/${id}/delete/`);
      fetchCustomertypes();
      // Adjust current page if we deleted the last item on the current page
      if (filteredCustomertypes.length % itemsPerPage === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (error) {
      setError(error.response?.data?.detail || "فشل في حذف نوع الزبون.");
    }
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCustomertypes.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCustomertypes.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  useEffect(() => {
    fetchCustomertypes();
  }, []);

  return (
    <Container style={{ direction: "rtl", textAlign: "right", padding: "20px", fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}>
      <h1 style={{ color: "darkgreen", marginBottom: "20px", fontWeight: "bold" }}>أنواع الزبائن</h1>

      {/* Search Bar */}
      <div className="mb-4" style={{ maxWidth: "400px" }}>
        <div className="input-group search-container" style={{ 
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          borderRadius: "40px",
          overflow: "hidden",
        }}>
          <input
            type="text"
            className="form-control search-input"
            placeholder="ابحث عن نوع زبون..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ 
              border: "1px solid #e0e0e0",
              borderRight: "none",
              padding: "12px 20px",
              fontSize: "16px",
              // transition: "all 0.3s ease",
              backgroundColor: "#f9f9f9",
              borderTopLeftRadius: "50px",
              borderBottomLeftRadius: "50px"
            }}
          />
          <span 
            className="input-group-text search-icon"
            style={{ 
              backgroundColor: "#43A047",
              color: "white",
              border: "none",
              borderTopRightRadius: "50px",
              borderBottomRightRadius: "50px",
              cursor: "pointer",
              padding: "0 20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <FaSearch size={18} />
          </span>
        </div>
      </div>
      <style jsx="true">{`
        .search-container:hover {
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
          transform: translateY(-2px);
        }
        .search-input:focus {
          background-color: #ffffff !important;
          box-shadow: none !important;
          border-color: #43A047 !important;
          outline: none !important;
        }
        .search-icon:hover {
          background-color: #2E7D32 !important;
        }
      `}</style>

      {/* Add Expense Form */}
      <Form.Group className="mb-3">
        <Form.Control
          ref={nameRef}
          type="text"
          placeholder="أدخل اسم نوع الزبون"
          value={newExpenseName}
          onChange={(e) => setNewExpenseName(e.target.value)}
          style={{ marginBottom: "10px", borderColor: "darkgreen" }}
        />
        <Button 
          onClick={addExpense}
          style={{ 
            backgroundColor: "darkgreen",
            borderColor: "darkgreen"
          }}
        >
          إضافة نوع زبون
        </Button>
      </Form.Group>

      {/* Error Message */}
      {error && <Alert variant="danger">{error}</Alert>}

      {/* Loading Spinner */}
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <>
          {/* Customertypes Table */}
          <Table striped bordered hover responsive>
            <thead style={{ backgroundColor: "darkgreen", color: "white" }}>
              <tr>
                <th>#</th>
                <th>نوع الزبون</th>
                <th>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.length === 0 ? (
                <tr>
                  <td colSpan="3">لا توجد أنواع زبائن حاليا</td>
                </tr>
              ) : (
                currentItems.map((expense, index) => (
                  <tr key={expense.id}>
                    <td>{indexOfFirstItem + index + 1}</td>
                    <td>{expense.name}</td>
                    <td>
                      <Button
                        size="sm"
                        className="btn btn-info bg-info mx-1"
                        onClick={() => openEditModal(expense)}
                      >
                        تعديل
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => deleteExpense(expense.id)}
                        className="btn btn-danger bg-danger mx-1"
                      >
                        <i className="fas fa-trash" ></i> حذف
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          {filteredCustomertypes.length > itemsPerPage && (
            <Pagination className="justify-content-center">
              <Pagination.First 
                onClick={() => paginate(1)} 
                disabled={currentPage === 1} 
                style={{ color: "darkgreen" }}
              />
              <Pagination.Prev 
                onClick={() => paginate(currentPage - 1)} 
                disabled={currentPage === 1} 
                style={{ color: "darkgreen" }}
              />
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                <Pagination.Item 
                  key={number} 
                  active={number === currentPage}
                  onClick={() => paginate(number)}
                  style={{
                    backgroundColor: number === currentPage ? "darkgreen" : "white",
                    color: number === currentPage ? "white" : "darkgreen",
                    borderColor: "darkgreen"
                  }}
                >
                  {number}
                </Pagination.Item>
              ))}
              
              <Pagination.Next 
                onClick={() => paginate(currentPage + 1)} 
                disabled={currentPage === totalPages} 
                style={{ color: "darkgreen" }}
              />
              <Pagination.Last 
                onClick={() => paginate(totalPages)} 
                disabled={currentPage === totalPages} 
                style={{ color: "darkgreen" }}
              />
            </Pagination>
          )}
        </>
      )}

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton style={{ borderColor: "darkgreen" }}>
          <Modal.Title style={{ color: "darkgreen" }}>تعديل نوع الزبون</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            type="text"
            value={selectedExpense.name}
            onChange={(e) =>
              setSelectedExpense({ ...selectedExpense, name: e.target.value })
            }
            placeholder="أدخل الاسم الجديد"
            style={{ marginBottom: "10px", borderColor: "darkgreen" }}
          />
        </Modal.Body>
        <Modal.Footer style={{ borderColor: "darkgreen" }}>
          <Button 
            variant="secondary" 
            onClick={() => setShowEditModal(false)}
            style={{ 
              backgroundColor: "white", 
              color: "darkgreen", 
              borderColor: "darkgreen" 
            }}
          >
            إلغاء
          </Button>
          <Button 
            onClick={saveEditedExpense}
            style={{ 
              backgroundColor: "darkgreen",
              borderColor: "darkgreen"
            }}
          >
            حفظ
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CustomerTypes;